"use client";

import Link from "next/link";
import { signInHref, useEditionSlug } from "@/lib/onboarding/edition-link";

/**
 * What someone sees if sign-in, sign-up, or password reset throws.
 *
 * `(app)/error.tsx` exists for the same reason and explains it at length — this
 * is the sibling gap it left behind: `(auth)` is its own route segment, so
 * Next only walks up to the nearest `error.tsx` above IT, and there wasn't
 * one. Without this, an exception here fell through to Next's bare unbranded
 * crash screen, dropping someone mid sign-in onto a page with no way back
 * into the flow — for a visitor who doesn't have a workspace to return to yet,
 * that's worse than the same failure inside the app.
 *
 * `(auth)/layout.tsx` stays mounted around this (only its `{children}` slot is
 * replaced), so the QOD365 logo and the shared onboarding chrome survive —
 * this only needs to fill the card.
 */
export default function AuthError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // An error boundary receives no props and no searchParams, which is the exact
  // case `useEditionSlug` was written for — and this was the one place on the
  // auth surface still ignoring it, so a reseller's visitor met OUR brand at the
  // worst possible moment
  // (BUG-2026-09-11-the-password-reset-detour-drops-the-reseller-edition).
  const edition = useEditionSlug();
  return (
    <div className="ob-stagger">
      <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.02em]">Something went wrong</h1>
      <p className="mt-2 text-[15px] text-muted-foreground">
        The problem is ours, not something you did. Trying again often works, because most of
        these are momentary.
      </p>
      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={reset}
          className="ob-cta inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
        >
          Try again
        </button>
        <Link
          href={signInHref(edition)}
          className="inline-flex w-full items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted"
        >
          Back to sign in
        </Link>
      </div>
      {error.digest ? (
        <p className="mt-6 text-xs text-muted-foreground">
          If it keeps happening, quote this to support:{" "}
          <code className="font-mono">{error.digest}</code>
        </p>
      ) : null}
    </div>
  );
}
